/**
 * Lightweight error reporter — bridge towards Sentry.
 * Sends client errors to the `log-client-error` edge function which writes to `error_logs`.
 *
 * Design goals:
 * - Never throw, never block UI, never log secrets.
 * - Deduplicate identical errors within a short window.
 * - Honor user consent (skipped when analytics consent not granted).
 * - When Sentry is added later, swap the transport here without changing call sites.
 */

import { consentAllowsAnalytics } from "@/lib/consent";

// Derive the functions endpoint from VITE_SUPABASE_URL when the explicit
// project-id env var is missing (avoids `https://undefined.supabase.co/...`
// silently swallowing every log). VITE_SUPABASE_URL is always present.
const PROJECT_ID =
  (import.meta.env.VITE_SUPABASE_PROJECT_ID as string | undefined) ||
  (() => {
    try {
      return new URL(import.meta.env.VITE_SUPABASE_URL as string).hostname.split(".")[0];
    } catch {
      return "";
    }
  })();
const ENDPOINT = PROJECT_ID
  ? `https://${PROJECT_ID}.supabase.co/functions/v1/log-client-error`
  : "";
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const RELEASE = (import.meta.env.MODE ?? "development") + "@" + (import.meta.env.VITE_APP_VERSION ?? "dev");

const recent = new Map<string, number>();
const DEDUP_WINDOW_MS = 30_000;
const MAX_QUEUE = 20;
let sent = 0;

type Level = "error" | "warn" | "info";

interface ReportOptions {
  level?: Level;
  context?: Record<string, unknown>;
}

// AbortError appears whenever the browser cancels in-flight fetch/XHR calls
// during navigation, refresh, or bfcache restore. Treat it as benign noise
// (same policy as the boot handler in index.html).
function isBenignAbortError(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const r = value as { name?: string; code?: number; message?: string };
  if (r.name === "AbortError" || r.code === 20) return true;
  const msg = typeof r.message === "string" ? r.message : "";
  if (
    msg === "The operation was aborted." ||
    msg === "The user aborted a request." ||
    msg.includes("signal is aborted") ||
    (msg.includes("aborted") && msg.includes("fetch"))
  ) return true;
  return false;
}

function fingerprint(message: string, stack?: string): string {
  const head = (stack ?? "").split("\n").slice(0, 3).join("|");
  return `${message}::${head}`.slice(0, 300);
}

export function reportError(error: unknown, opts: ReportOptions = {}): void {
  if (sent >= MAX_QUEUE) return;
  if (typeof window === "undefined") return;
  if (!consentAllowsAnalytics()) return;

  const err = error instanceof Error ? error : new Error(String(error));
  const fp = fingerprint(err.message, err.stack);
  const now = Date.now();
  const last = recent.get(fp);
  if (last && now - last < DEDUP_WINDOW_MS) return;
  recent.set(fp, now);

  // Primary transport: Sentry (only initialized in production, gated by consent).
  // Best-effort dynamic import so dev/preview builds don't pay for it.
  if (import.meta.env.MODE === "production") {
    import("@sentry/react").then((Sentry) => {
      // Map our Level → Sentry SeverityLevel ("warn" → "warning")
      const sentryLevel = opts.level === "warn" ? "warning" : (opts.level ?? "error");
      Sentry.captureException(err, {
        level: sentryLevel as "error" | "warning" | "info",
        contexts: { app: { ...(opts.context ?? {}) } },
      });
    }).catch(() => { /* swallow */ });
  }

  const payload = {
    message: err.message,
    stack: err.stack,
    url: window.location.href,
    user_agent: navigator.userAgent,
    level: opts.level ?? "error",
    context: opts.context ?? {},
    release: RELEASE,
  };

  sent++;
  // Backup transport: write to error_logs DB (works even if Sentry is down).
  // Fire-and-forget, never blocks UI. Skipped if we couldn't derive the
  // endpoint (no project id available) — Sentry path above still runs.
  if (ENDPOINT) {
    fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Swallow: we never want logging to throw
    });
  }
}

let installed = false;
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    const err = event.error ?? new Error(event.message);
    if (isBenignAbortError(err)) return;
    reportError(err, {
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason ?? new Error("Unhandled rejection");
    if (isBenignAbortError(reason)) return;
    reportError(reason, {
      context: { type: "unhandledrejection" },
    });
  });
}
